import { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react'

export interface UsePullToRefreshOptions {
    /** Async callback invoked when the user releases past the threshold */
    onRefresh: () => Promise<void> | void
    /** Minimum pull distance (px) to trigger a refresh. Default: 80 */
    threshold?: number
    /** Maximum pull distance (px). Default: 150 */
    maxPull?: number
    /** Disable the gesture (e.g. while already refreshing via button). Default: false */
    disabled?: boolean
}

export interface UsePullToRefreshReturn {
    /** Current pull distance in pixels (0 when idle) */
    pullDistance: number
    /** True while the onRefresh callback is running */
    isRefreshing: boolean
    /** Attach to the scrollable container element */
    containerRef: React.RefObject<HTMLDivElement | null>
}

/**
 * Adds pull-to-refresh gesture support for touch devices.
 *
 * The gesture only activates when the page is scrolled to the very top
 * (`window.scrollY <= 1`) and the user swipes downward. Once the pull
 * exceeds `threshold`, releasing the finger triggers `onRefresh`.
 */
export function usePullToRefresh({
    onRefresh,
    threshold = 80,
    maxPull = 150,
    disabled = false,
}: UsePullToRefreshOptions): UsePullToRefreshReturn {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const [pullDistance, setPullDistance] = useState(0)
    const [isRefreshing, setIsRefreshing] = useState(false)

    // Refs for touch tracking (avoids re-renders on every move)
    const touchStartY = useRef(0)
    const isPulling = useRef(false)
    // Mirror pullDistance in a ref so handleTouchEnd always reads the latest value
    // without needing pullDistance in its dependency array (which would cause
    // event listener churn on every touchmove)
    const pullDistanceRef = useRef(0)

    // Sync latest props/state into refs so handlers stay stable
    const disabledRef = useRef(disabled)
    const isRefreshingRef = useRef(isRefreshing)
    const onRefreshRef = useRef(onRefresh)
    useLayoutEffect(() => {
        disabledRef.current = disabled
        isRefreshingRef.current = isRefreshing
        onRefreshRef.current = onRefresh
    })

    const handleTouchStart = useCallback((e: TouchEvent) => {
        if (disabledRef.current || isRefreshingRef.current) return
        if (window.scrollY > 1) return

        touchStartY.current = e.touches[0].clientY
        isPulling.current = true
    }, [])

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (!isPulling.current || disabledRef.current || isRefreshingRef.current) return

            const currentY = e.touches[0].clientY
            const diff = currentY - touchStartY.current

            // Only pull downward; if user scrolls up, cancel
            if (diff <= 0) {
                pullDistanceRef.current = 0
                setPullDistance(0)
                return
            }

            // Abort if the page has been scrolled (e.g. momentum from earlier)
            if (window.scrollY > 1) {
                isPulling.current = false
                pullDistanceRef.current = 0
                setPullDistance(0)
                return
            }

            // Apply resistance: diminishing returns past threshold
            const resistance = diff < threshold ? 1 : 0.4
            const distance = Math.min(
                diff < threshold ? diff : threshold + (diff - threshold) * resistance,
                maxPull
            )

            pullDistanceRef.current = distance
            setPullDistance(distance)
        },
        [threshold, maxPull]
    )

    const handleTouchEnd = useCallback(async () => {
        if (!isPulling.current) return
        isPulling.current = false

        if (pullDistanceRef.current >= threshold && !isRefreshingRef.current) {
            setIsRefreshing(true)
            pullDistanceRef.current = threshold
            setPullDistance(threshold)
            try {
                await onRefreshRef.current()
            } catch {
                // Swallow refresh errors — the indicator resets regardless
            } finally {
                setIsRefreshing(false)
                pullDistanceRef.current = 0
                setPullDistance(0)
            }
        } else {
            pullDistanceRef.current = 0
            setPullDistance(0)
        }
    }, [threshold])

    useEffect(() => {
        const el = containerRef.current
        if (!el) return

        el.addEventListener('touchstart', handleTouchStart, { passive: true })
        el.addEventListener('touchmove', handleTouchMove, { passive: true })
        el.addEventListener('touchend', handleTouchEnd)
        el.addEventListener('touchcancel', handleTouchEnd)

        return () => {
            el.removeEventListener('touchstart', handleTouchStart)
            el.removeEventListener('touchmove', handleTouchMove)
            el.removeEventListener('touchend', handleTouchEnd)
            el.removeEventListener('touchcancel', handleTouchEnd)
        }
    }, [handleTouchStart, handleTouchMove, handleTouchEnd])

    return { pullDistance, isRefreshing, containerRef }
}
